import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { withTheme } from 'styled-components'
import { Grid, Row, Col, Panel } from 'react-bootstrap'
import { Helmet } from 'react-helmet'
import ReactPlayer from 'react-player'

import MoreIcon from '../../assets/icons/expand_more.svg'
import { GettingStartedContainer } from '../../containers'
import PopoutIframeModal from '../PopoutIframeModal/PopoutIframeModal'
import LanguageProvider from '../../containers/LanguageProvider/LanguageProvider'
import { OptionsHOC } from '../../hocs'
import { useAppContext } from '../../containers/App/Context'
import SEO from '../SEO'

import messages from './messages'
import { Wrapper } from './styles'
import SpecContent from './components/SpecContent'

const listAllFeatures = features => {
  return Object.entries(features).map(([key, value]) => {
    // if feature has key and value, then key is a name
    // of feature and value is a value of feature, otherwise key is a name of feature and value is an string "Yes"
    return `
            {
            "@type": "PropertyValue",
            "name": ${
              isNaN(key) ? JSON.stringify(key) : JSON.stringify(value.feature)
            },
            "value": ${
              !isNaN(key)
                ? JSON.stringify('Yes')
                : typeof value === 'object'
                ? JSON.stringify(value.feature)
                : JSON.stringify(value)
            }
            }
        `
  })
}
const listContent = content => {
  return content.map((item, index) => {
    return `
            {
            "@type": "ListItem",
            "position": "${index + 1}",
            "item": {
                "@type": "HowToStep",
                "name": "${item.contentTitle}"
            }
        }
        `
  })
}

const productStructuredData = (product, content) => {
  const { featurevaluepairs, phone, image } = product
  if (!featurevaluepairs && !content) return null
  const { size, weight, ...rest } = featurevaluepairs.reduce(
    (list, feature) => {
      list[feature.featuretype.toLowerCase()] = feature.feature
      return list
    },
    {}
  )
  return `
      {
          "@context": "https://schema.org",
          "@type": ["Product", "ItemList"],
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "5.0",
            "reviewCount": "11"
          },
          "name": "${phone && phone.name_pretty}",
          "image": "${image && image.url}",
          "model": "${phone && phone.name}",
          "size": ${JSON.stringify(size)},
          "weight": ${JSON.stringify(weight)},
          "additionalProperties": [${listAllFeatures({
            ...rest,
            ...product.features
          })}],
          "itemListElement": [${listContent(content)}],
          "brand": {
              "@type": "Brand",
              "name": "${phone && phone.manufacturer}"}
           }
  `
}

const proptypes = {
  currentPhoneInfo: PropTypes.object.isRequired,
  getOption: PropTypes.func.isRequired
}

const FeatureItem = ({ feature, index }) => (
  <li className="spec-li" key={index}>
    <span>{feature.url ? (<a href={feature.url} target='_blank'>{feature.feature}</a>) : feature.feature}</span>
  </li>
)

const SpecItem = ({ spec, index }) => (
  <li className="spec-li" key={index}>
    <span>
      {spec.featuretype} — {spec.feature}
    </span>
  </li>
)

const RenderFeatures = features => {
  return features.map((feature, index) => (
    <FeatureItem feature={feature} index={index} key={index} />
  ))
}

const RenderSpecs = specs => {
  return specs.map((spec, index) => (
    <SpecItem spec={spec} index={index} key={index} />
  ))
}

const Specs = ({
  currentPhoneInfo,
  createUserEvent,
  getOption,
  language,
  startercontent
}) => {
  const [showManualModal, setShowManualModal] = useState(false)
  const [showBookletModal, setShowBookletModal] = useState(false)

  const translated = messages[language]
  const title =
    Object.keys(currentPhoneInfo).length === 0
      ? ''
      : `${currentPhoneInfo.phone.name_pretty}`
  const manual = currentPhoneInfo.manual ? currentPhoneInfo.manual.url : ''
  const booklet = currentPhoneInfo.booklet ? currentPhoneInfo.booklet.url : ''

  const appleUrl = url => url.includes('apple.com')

  const closeModal = action => {
    createUserEvent({
      category: 'specs',
      data: { action: action }
    })
    setShowManualModal(false)
    setShowBookletModal(false)
  }

  const openModal = action => {
    createUserEvent({
      category: 'specs',
      data: { action: action }
    })

    action === 'ShowUserManual'
      ? setShowManualModal(true)
      : setShowBookletModal(true)
  }

  const { versionKey: version } = useAppContext()

  const renderList = (items, trigger, type, open) => {
    if (items.length) {
      return (
        <Panel defaultExpanded={open} key={Math.uuid()}>
          <Panel.Heading>
            <Panel.Title>
              <Panel.Toggle>
                {trigger}
                {version === 'v4' && (
                  <img src={MoreIcon} alt="collapse_icon" />
                )}
              </Panel.Toggle>
            </Panel.Title>
          </Panel.Heading>
          <Panel.Collapse
            onEnter={() =>
              createUserEvent({
                category: 'specs',
                data: { action: 'Toggle' + type }
              })
            }
          >
            <Panel.Body>
              <ul className="double">{items}</ul>
            </Panel.Body>
          </Panel.Collapse>
        </Panel>
      )
    }
  }

  return (
    <div className="animated fadeIn">
      <If condition={currentPhoneInfo.phone}>
        <SEO
          title={`${currentPhoneInfo.phone.name_pretty}`}
          image={currentPhoneInfo.image.url}
          script={productStructuredData(currentPhoneInfo, startercontent)}
          description={`${currentPhoneInfo.phone.name_pretty}: device overview, features, popular tutorials and troubleshooting guides.`}
        />
        <Helmet>
          <link rel="canonical" href={`${window.location.href}`} />
        </Helmet>
      </If>
      <Wrapper version={version}>
        <Grid id="specs-grid">
          <PopoutIframeModal
            show={showManualModal}
            onHide={() => {
              closeModal('HideUserManual')
            }}
            closeModal={closeModal}
            title={title}
            link={manual}
            appleUrl={appleUrl(manual)}
            createUserEvent={createUserEvent}
          />
          <PopoutIframeModal
            show={showBookletModal}
            onHide={() => {
              closeModal('HideBooklet')
            }}
            closeModal={closeModal}
            title={title}
            link={booklet}
            appleUrl={appleUrl(booklet)}
            createUserEvent={createUserEvent}
          />
          <SpecContent
            device={currentPhoneInfo}
            language={language}
            getOption={getOption}
            openModal={openModal}
            createUserEvent={createUserEvent}
            RenderFeatures={RenderFeatures}
            RenderSpecs={RenderSpecs}
            renderList={renderList}
            version={version}
          />
          <GettingStartedContainer />
        </Grid>
      </Wrapper>
    </div>
  )
}

Specs.propTypes = proptypes
export default withTheme(LanguageProvider(OptionsHOC(Specs)))



// WEBPACK FOOTER //
// ./src/components/Specs/Specs.js